import * as React from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'redux';
import { Drawer, Checkbox } from 'components/';
import * as Queries from 'api/graphql/queries';
import { OrderDirection, adminProductListAdsQuery, adminProductListAdsQuery_adminProductListAds_data } from '@api/graphql/types';
import GraphQLClient from '@api/graphql/GraphQLClient';
import ControlledTable from '@components/ControlledTable';
import { RowInfo, CellInfo } from 'react-table';
import { Column } from 'react-virtualized';
import { BooleanCell, OpenInNewCell } from '@components/Table/Cell';
import moment from 'moment';
import Colors from '@root/utils/Colors';
import { DispatchProp, connect } from 'react-redux';
import { updateListOptions } from '@actions/listActions';
import { ListOptionType } from '@reducers/listReducer';
import { ApplicationState } from 'reducers/';
import { Paths } from '..';

interface StateType {
    data: any[];
    paginatorInfo: any;
    shouldFilterActive: boolean;
}

const ProductListAdListDefaultOptions = {
    sortBy: {
        field: 'id',
        direction: OrderDirection.DESC,
    },
    count: 10,
    page: 0,
    filters: {
        id: '',
        name: '',
        email: '',
    },
};

const listName = 'productListAdList';

interface ReduxProps {
    listOptions: ListOptionType;
}

type Props = ReduxProps & DispatchProp & InjectedIntlProps & RouteComponentProps;

class ProductListAdList extends React.Component<Props, StateType> {
    public state = {
        data: [],
        paginatorInfo: {},
        shouldFilterActive: true,
    };

    private InactiveDisplay = (state: any, rowInfo: RowInfo, column: Column) => {
        if (rowInfo) {
            return this.isDateActive(rowInfo.row) ? {} : { style: { color: Colors.inactive } };
        }
        return {};
    }

    private isDateActive = (row: adminProductListAdsQuery_adminProductListAds_data): boolean => {
        return ((row.is_active) && (!row.active_from || (row.active_from && moment(row.active_from).isBefore(moment()))) && (!row.active_to || (row.active_to && moment(row.active_to).isAfter(moment()))));
    }

    private OpenCell = OpenInNewCell((rowInfo: any) => {
        return `${Paths.productListAd}/${rowInfo.original.id}`;
    });

    private tableColumns = [
        {Header: '', sortable: false, Cell: this.OpenCell, width: 30},
        this.getDefaultColumnByName('id', {sortable: true, style: {width: '50px'}}),
        this.getDefaultColumnByName('name', {sortable: true}),
        this.getDefaultColumnByName('is_active', {Cell: BooleanCell}),
        this.getDefaultColumnByName('active_from', {sortable: true}),
        this.getDefaultColumnByName('active_to', {sortable: true}),
        this.getDefaultColumnByName('urls', {sortable: true,
            Cell: (cellInfo: CellInfo): any => (<span>{cellInfo.original.urls.length}</span>),
        }),
    ];

    public componentDidMount() {
        this.fetchData();
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.listOptions !== this.props.listOptions) {
            this.fetchData();
        }
    }

    private getDefaultColumnByName(name: string, options: any = {}) {
        return {
            Header: this.label(`${name}.title`),
            accessor: name,
            sortable: false,
            getProps: this.InactiveDisplay,
            ...options,
        };
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.productListAd.${id}` });
    }

    private getSortBy() {
        return {
            field: this.props.listOptions.sortBy.field.toUpperCase(),
            direction: this.props.listOptions.sortBy.direction.toUpperCase(),
        };
    }

    private fetchData() {
        const variables = {...this.props.listOptions, page: this.props.listOptions.page + 1, sortBy: this.getSortBy(), filters: { is_active: this.state.shouldFilterActive || undefined } };
        GraphQLClient.query<adminProductListAdsQuery>({
            query: Queries.adminProductListAdsQuery,
            variables,
        }).then(response => {
            const data = response.data.adminProductListAds ? response.data.adminProductListAds.data : [];
            const paginatorInfo = response.data.adminProductListAds ? response.data.adminProductListAds.paginatorInfo : {};
            this.setState({data, paginatorInfo});
        });
    }

    private onOptionsChange = (options: ListOptionType) => {
        this.props.dispatch(updateListOptions(listName, options));
    }

    private onRowClick = (finalState: any, rowInfo?: RowInfo, column?: Column, instance?: any): object | undefined => {
        if (rowInfo) {
            return {
                onClick: () => {
                    this.props.history.push(`${Paths.productListAd}/${rowInfo.original.id}`);
                },
                style: {
                    cursor: 'pointer',
                },
            };
        }
        return {};
    }


    public render() {
        const addButton = <button className="btn btn-primary" onClick={(e: React.SyntheticEvent) => this.props.history.push(Paths.productListAdCreate)}>{this.label("newProductListAd")}</button>;

        return (
            <Drawer headerButton={addButton}>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-content">
                                <Checkbox
                                    checked={this.state.shouldFilterActive}
                                    label={this.props.intl.formatMessage({id: 'common.filterActive'})}
                                    onChange={(e: React.ChangeEvent<any>) => {
                                        this.setState({ shouldFilterActive: e.currentTarget.checked }, () => this.fetchData());
                                    }}
                                />
                                <ControlledTable
                                    listName={listName}
                                    defaultOptions={ProductListAdListDefaultOptions}
                                    columns={this.tableColumns}
                                    options={this.props.listOptions}
                                    data={this.state.data}
                                    paginatorInfo={this.state.paginatorInfo}
                                    onOptionsChange={this.onOptionsChange}
                                    onRowClick={this.onRowClick}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer>
        );
    }
}

const mapStateToProps = ({ lists }: ApplicationState): ReduxProps => {
    return { listOptions: (lists ? (lists[listName] || ProductListAdListDefaultOptions) : ProductListAdListDefaultOptions) };
};

export default compose(
    withRouter,
    injectIntl,
    connect(mapStateToProps),
)(ProductListAdList);



// WEBPACK FOOTER //
// ./src/modules/ProductListAd/pages/ProductListAdList.tsx